import { ImageOff } from "lucide-react";
import { Check, X } from "lucide-react";

export const TabelBooleanText = ({
  value = false,
  onChange,
  disabled = false,
  showLabel = false,
  trueText = "Active",
  falseText = "Inactive",
  size = "sm", // 'xs' | 'sm' | 'md'
  variant = "switch", // 'switch' | 'badge' | 'icon'
  className = "",
}) => {
  const sizes = {
    xs: {
      track: "w-7 h-4",
      thumb: "w-3 h-3",
      translate: "translate-x-3",
      icon: "w-2.5 h-2.5",
      badge: "px-2 py-0.5 text-[10px]",
    },
    sm: {
      track: "w-9 h-5",
      thumb: "w-4 h-4",
      translate: "translate-x-4",
      icon: "w-3 h-3",
      badge: "px-2.5 py-0.5 text-xs",
    },
    md: {
      track: "w-11 h-6",
      thumb: "w-5 h-5",
      translate: "translate-x-5",
      icon: "w-3.5 h-3.5",
      badge: "px-3 py-1 text-xs",
    },
  };

  const currentSize = sizes[size] || sizes.sm;

  if (variant === "badge") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 font-medium rounded-full border transition-colors select-none ${
          currentSize.badge
        } ${
          value
            ? "bg-emerald-50 text-emerald-700 border-emerald-200/60 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
            : "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
        } ${className}`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${
            value ? "bg-emerald-500 animate-pulse" : "bg-gray-400"
          }`}
        />
        {value ? trueText : falseText}
      </span>
    );
  }

  if (variant === "icon") {
    return (
      <div
        className={`inline-flex items-center justify-center rounded-full p-1 ${
          value
            ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400"
            : "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
        } ${className}`}
      >
        {value ? (
          <Check className={currentSize.icon} />
        ) : (
          <X className={currentSize.icon} />
        )}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={disabled || !onChange}
        onClick={() => onChange && onChange(!value)}
        className={`relative inline-flex shrink-0 items-center rounded-full p-0.5 transition-all duration-200 ease-in-out focus:outline-none ${
          currentSize.track
        } ${
          value
            ? "bg-primary dark:bg-indigo-500"
            : "bg-gray-200 dark:bg-gray-700"
        } ${
          disabled || !onChange
            ? "cursor-default opacity-80"
            : "cursor-pointer focus-visible:ring-2 focus-visible:ring-indigo-500/30 focus-visible:ring-offset-1"
        }`}
      >
        {/* Sliding Thumb */}
        <span
          className={`pointer-events-none flex items-center justify-center rounded-full bg-white shadow-xs transition-transform duration-200 ease-in-out ${
            currentSize.thumb
          } ${value ? currentSize.translate : "translate-x-0"}`}
        >
          {/* Subtle micro-icons inside the switch thumb */}
          {value ? (
            <Check className="w-2.5 h-2.5 text-primary stroke-[3]" />
          ) : (
            <X className="w-2.5 h-2.5 text-gray-400 stroke-[3]" />
          )}
        </span>
      </button>

      {/* Optional inline status text */}
      {showLabel && (
        <span
          className={`text-xs font-medium ${
            value
              ? "text-gray-900 dark:text-gray-100"
              : "text-gray-400 dark:text-gray-500"
          }`}
        >
          {value ? trueText : falseText}
        </span>
      )}
    </div>
  );
};

export const TabelArrayText = ({ arrayData = [] }) => {
  if (!arrayData || arrayData.length === 0) {
    return <span className="text-gray-400 font-medium">--</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {arrayData.map((ele, index) => (
        <span
          key={index}
          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 capitalize"
        >
          {ele || "--"}
        </span>
      ))}
    </div>
  );
};

export const TabelImage = ({ url }) => {
  return (
    <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-gray-200  bg-gray-50shadow-sm">
      {url ? (
        <img
          src={url}
          alt={"lazy"}
          className="h-full w-full object-cover transition-opacity duration-200"
          loading="lazy"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-gray-400">
          <ImageOff className="h-5 w-5 stroke-[1.5]" />
        </div>
      )}
    </div>
  );
};
